import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Zap, AlertCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * CreditsDisplay Component
 * Shows user's remaining credits/usage for AI queries
 * Automatically refreshes and updates when queries are made
 */
const CreditsDisplay = ({ onCreditsUpdate, refreshTrigger = 0 }) => {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCredits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/user/rate-limit-status`, {
        withCredentials: true
      });
      
      if (response.data.status === 'success') {
        setCredits(response.data);
        if (onCreditsUpdate) {
          onCreditsUpdate(response.data);
        }
      } else {
        setError('Could not fetch credits');
      }
    } catch (err) {
      console.error('Error fetching credits:', err);
      // Don't show error if user is not authenticated yet
      if (err.response?.status !== 401) {
        setError('Failed to load credits');
      }
    } finally {
      setLoading(false);
    }
  }, [onCreditsUpdate]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits, refreshTrigger]);

  // Expose refresh function via window for other components to call
  useEffect(() => {
    window.refreshCredits = fetchCredits;
    return () => {
      delete window.refreshCredits;
    };
  }, [fetchCredits]);

  if (loading && !credits) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingPill}>
          <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !credits) {
    return null; // Don't show anything if there's an error
  }

  const { llm_queries, search_queries } = credits;
  const totalUsed = llm_queries.used + search_queries.used;
  const totalLimit = llm_queries.limit + search_queries.limit;
  const totalRemaining = llm_queries.remaining + search_queries.remaining;
  
  // Calculate percentage for color coding
  const usagePercentage = (totalUsed / totalLimit) * 100;
  const isLow = usagePercentage >= 80;
  const isCritical = usagePercentage >= 95;

  const getStatusColor = () => {
    if (isCritical) return '#ef4444'; // Red
    if (isLow) return '#f59e0b'; // Orange/Amber
    return '#10b981'; // Green
  };

  const getBackgroundColor = () => {
    if (isCritical) return 'rgba(239, 68, 68, 0.1)';
    if (isLow) return 'rgba(245, 158, 11, 0.1)';
    return 'rgba(16, 185, 129, 0.1)';
  };

  return (
    <div style={styles.container}>
      <div 
        style={{
          ...styles.creditsPill,
          backgroundColor: getBackgroundColor(),
          borderColor: getStatusColor()
        }}
        title={`AI Credits: ${llm_queries.remaining}/${llm_queries.limit} | Search: ${search_queries.remaining}/${search_queries.limit}`}
      >
        <Zap size={16} color={getStatusColor()} />
        <span style={{ ...styles.creditsText, color: getStatusColor() }}>
          {totalRemaining} credits left
        </span>
        {isLow && !isCritical && (
          <AlertCircle size={14} color={getStatusColor()} />
        )}
        {isCritical && (
          <AlertCircle size={14} color={getStatusColor()} style={{ animation: 'pulse 1s infinite' }} />
        )}
      </div>
      
      {/* Detailed breakdown on hover/click */}
      <div style={styles.tooltip}>
        <div style={styles.tooltipHeader}>Daily Usage</div>
        <div style={styles.tooltipRow}>
          <span>🧠 AI Queries:</span>
          <span style={{ color: llm_queries.remaining === 0 ? '#ef4444' : '#10b981' }}>
            {llm_queries.used}/{llm_queries.limit}
          </span>
        </div>
        <div style={styles.tooltipRow}>
          <span>🔍 Searches:</span>
          <span style={{ color: search_queries.remaining === 0 ? '#ef4444' : '#10b981' }}>
            {search_queries.used}/{search_queries.limit}
          </span>
        </div>
        <div style={styles.tooltipNote}>
          Resets daily at midnight IST
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  creditsPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1px solid',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  creditsText: {
    fontWeight: '600',
  },
  loadingPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontSize: '13px',
    color: '#666',
  },
  tooltip: {
    position: 'absolute',
    top: '100%',
    right: '0',
    marginTop: '8px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    minWidth: '180px',
    zIndex: 1000,
    opacity: 0,
    visibility: 'hidden',
    transform: 'translateY(-10px)',
    transition: 'all 0.2s ease',
    pointerEvents: 'none',
  },
  tooltipHeader: {
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '8px',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '6px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    marginBottom: '4px',
    color: '#555',
  },
  tooltipNote: {
    fontSize: '11px',
    color: '#888',
    marginTop: '8px',
    fontStyle: 'italic',
  },
};

// Add hover effect styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  .credits-container:hover .credits-tooltip {
    opacity: 1 !important;
    visibility: visible !important;
    transform: translateY(0) !important;
    pointer-events: auto !important;
  }
`;
document.head.appendChild(styleSheet);

export default CreditsDisplay;
